import { useEffect, useState } from 'react';
import { Ticket, Wallet } from 'lucide-react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../app/services';
import type { BudgetMember, BudgetSpace } from '../../domain/model';
import { useSync } from '../../sync/SyncProvider';
import { AvatarStack, ButtonLink, Card, EmptyState, LoadingState, ScreenTitle, useFormat } from '../../ui';
import { formatMoney } from '../../i18n/format';
import { findRegion } from '../../i18n/regions';
import '../sharing/sharing.css';

/** Every budget you are in; a budget opens its people (invite, remove, leave). */
export function BudgetsScreen() {
  const { t } = useTranslation();
  const { store, identity, activeSpace, dataVersion } = useServices();
  const sync = useSync();
  const { formatLocale } = useFormat();
  const [spaces, setSpaces] = useState<BudgetSpace[] | null>(null);
  const [members, setMembers] = useState<Map<string, BudgetMember[]>>(new Map());

  useEffect(() => {
    let live = true;
    void store.spaces.listForUser(identity.session.userId).then(async (list) => {
      const people = await Promise.all(list.map((s) => store.members.list({ spaceId: s.id }, { orderBy: 'created_at' })));
      if (!live) return;
      setSpaces(list);
      setMembers(new Map(list.map((s, i) => [s.id, people[i]!])));
    });
    return () => {
      live = false;
    };
  }, [store, identity, activeSpace, dataVersion]);

  return (
    <>
      <ScreenTitle>{t('nav.budgets')}</ScreenTitle>
      {spaces === null ? (
        <LoadingState rows={2} />
      ) : spaces.length === 0 ? (
        <EmptyState icon={Wallet} title={t('budgets.emptyTitle')} body={t('budgets.emptyBody')} />
      ) : (
        <div className="stack" data-testid="budget-list">
          {spaces.map((s) => {
            const locale = (s.billingCountry && findRegion(s.billingCountry)?.formatLocale) || formatLocale;
            const people = members.get(s.id) ?? [];
            return (
              <Link key={s.id} to={`/budgets/${s.id}`} className="budget-card-link" aria-label={s.name}>
                <Card as="article" className="stack stack-tight">
                  <div className="card-head">
                    <div>
                      <h2 className="card-title">{s.name}</h2>
                      <p className="card-subtitle">
                        {t(`spaces.type.${s.type}`)} · {s.currency}
                      </p>
                    </div>
                    {s.id === activeSpace?.id && <span className="pill">{t('budgets.active')}</span>}
                  </div>
                  <div className="card-foot budget-card-foot">
                    <span className="num">
                      {s.monthlyTargetMinor
                        ? t('budgets.target', { amount: formatMoney(s.monthlyTargetMinor, s.currency, locale, { whole: true }) })
                        : t('budgets.noTarget')}
                    </span>
                    {people.length > 1 && (
                      <span className="budget-card-people">
                        <AvatarStack names={people.map((m) => m.displayName)} />
                        <span>{t('sharing.sharedWith', { count: people.length })}</span>
                      </span>
                    )}
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
      {sync.available && (
        <div className="screen-actions">
          <ButtonLink to="/join" variant="secondary" size="lg" block icon={Ticket}>
            {t('sharing.joinWithCode')}
          </ButtonLink>
        </div>
      )}
    </>
  );
}
